"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WARD_TYPE_LABELS = exports.createIpWardTransferSchema = exports.ipWardTransferRegisterQuerySchema = exports.ipWardTransferLookupQuerySchema = exports.IP_BILL_NUMBER_MESSAGE = exports.IP_BILL_NUMBER_PATTERN = void 0;
exports.wardTypeLabel = wardTypeLabel;
const zod_1 = require("zod");
/**
 * WARD TRANSFER (desk, 2026-09-30) — the IP Admission page's "Ward Transfer" tab (asked for first on the Cash Counter
 * page, then moved here).
 *
 * "User will enter the IP BILL Number only. It should show the present admitted ward, and a dropdown of the wards
 * with their type. After selecting, a preview of the present ward to the shifting ward, then confirm. A register to
 * check datewise, bill numberwise, who did it and when."
 *
 * THE STAY IS FOUND BY ITS BILL NUMBER AND NOTHING ELSE — exactly, not as a search. A partial number resolving to
 * "the first match" would move the wrong patient; the lookup either finds the one stay or says it found none.
 *
 * THE BILL NUMBER IS EXACTLY SIX DIGITS (desk, 2026-09-30: "only 6 digit numeric only"). Enforced here, so the API
 * refuses anything else, and again in the box, which drops every non-digit as it is typed.
 *
 * WHAT A TRANSFER CHANGES: the stay's ward, and one `WARD_TRANSFER` movement row (who, when, from, to). Nothing else:
 * not the stay's charge tier, not any captured charge. A line already priced keeps the price it was priced at.
 *
 * No permission family of its own (desk: "no permission restriction — whoever has the page"): the server accepts whoever
 * the IP Admission ROUTE itself accepts, `ip:admission:admit`. See `ip-admission.routes.ts`.
 */
/** True for a day that exists: `2026-02-31` has the right SHAPE and is not one (JavaScript would quietly read it as 3 March). */
function isRealDay(value) {
    const [y, m, d] = value.split('-').map(Number);
    const parsed = new Date(Date.UTC(y, m - 1, d));
    return parsed.getUTCFullYear() === y && parsed.getUTCMonth() === m - 1 && parsed.getUTCDate() === d;
}
const isoDate = zod_1.z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-30')
    .refine(isRealDay, 'That is not a real date');
/** An IP bill number as the desk writes it: six digits, nothing else. Exported so the screen applies the same rule. */
exports.IP_BILL_NUMBER_PATTERN = /^\d{6}$/;
exports.IP_BILL_NUMBER_MESSAGE = 'Enter the 6-digit IP bill number (numbers only)';
const billNumberField = zod_1.z.string().trim().regex(exports.IP_BILL_NUMBER_PATTERN, exports.IP_BILL_NUMBER_MESSAGE);
/** `GET /admissions/ward-transfers/lookup` — the whole bill number, nothing partial. */
exports.ipWardTransferLookupQuerySchema = zod_1.z.object({
    billNumber: billNumberField,
});
/**
 * `GET /admissions/ward-transfers` — the register. `from`/`to` are hospital-local (IST) calendar days, both
 * inclusive; `billNumber`, when given, is the same whole six-digit number and narrows the register to that one bill.
 */
exports.ipWardTransferRegisterQuerySchema = zod_1.z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    billNumber: billNumberField.optional(),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * `POST /admissions/:id/ward-transfer`. `lockVersion` is the one the lookup returned: if anything moved the stay in
 * between, the transfer is refused and the operator looks the bill up again — so what was previewed is exactly what
 * is applied.
 */
exports.createIpWardTransferSchema = zod_1.z.object({
    toWardId: zod_1.z.string().uuid(),
    lockVersion: zod_1.z.number().int().positive(),
    remarks: zod_1.z.string().trim().max(500).optional(),
});
/** The wording of a `WardType` code, for the dropdown's second line and the preview. */
exports.WARD_TYPE_LABELS = {
    GENERAL: 'General',
    CRITICAL_CARE: 'Critical care',
    SURGICAL: 'Surgical',
    MATERNITY: 'Maternity',
    SPECIALITY: 'Speciality',
    DAY_CARE: 'Day care',
};
function wardTypeLabel(type) {
    return exports.WARD_TYPE_LABELS[type] ?? type.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}
//# sourceMappingURL=ip-ward-transfer.schema.js.map